import { useCallback } from 'react';
import { gamificationStore } from '../../../utils/gamificationStore';
import { lessonGamificationService } from '../services/lesson-gamification.service';
import { Interaction, InteractionState } from '../types/lesson.types';
import { useUserStore } from '../../../utils/userStore';
import { playAppAudio, isAppAudioEnabled } from '../../../utils/audioManager';
import winSound from '../../../assets/audio/winpoints.mp3';

export const useLessonEngine = ({
  interactions,
  currentInteractionIndex,
  setCurrentInteractionIndex,
  interactionStates,
  interactionStatesRef,
  setInteractionStates,
  chatThreads,
  lessonData,
  totalItems,
  setIsResuming,
  setIsGreetingDone,
  setShowInteractions,
  updateProgress,
  debouncedSync,
  syncToDB,
  getFilteredStates,
  navigate,
  ROUTERS_PATH,
  fullTopic,
  levelName,
  currentProgress
}: any) => {
  const playWinSound = () => {
    const { isLoggedIn, user_attributes } = useUserStore.getState() as any;
    const isAudioEnabled = user_attributes?.isEnableAudio !== false;

    if (isLoggedIn && isAudioEnabled) {
      playAppAudio(winSound);
    }
  };

  const calcLearningTime = (endIdx: number, fromStart = false, states = interactionStatesRef.current) => {
    let startIdx = 0;
    if (!fromStart) {
      // Find start of current phase
      for (let i = endIdx - 1; i >= 0; i--) {
        if (interactions[i].isPhaseStop) { startIdx = i + 1; break; }
      }
    }

    let totalMs = 0;
    for (let i = startIdx; i <= endIdx; i++) {
      const item = interactions[i];
      if (!item) continue;
      const state = states[item.id];
      if (state?.startedAt) {
        // current interaction might not have completedAt yet, so use now
        const end = state.completedAt || Date.now();
        totalMs += end - state.startedAt;
      }
    }
    return totalMs;
  };

  const calcAccuracy = (endIdx: number, fromStart = false, states = interactionStatesRef.current) => {
    let startIdx = 0;
    if (!fromStart) {
      for (let i = endIdx - 1; i >= 0; i--) {
        if (interactions[i].isPhaseStop) { startIdx = i + 1; break; }
      }
    }

    const phaseInteractions = interactions.slice(startIdx, endIdx + 1);
    const quizzes = phaseInteractions.filter((item: any) => item.type === 'quiz');
    if (quizzes.length === 0) return 100; // Default to 100 if no quizzes

    let correctCount = 0;
    quizzes.forEach((quizItem: any) => {
      const state = states[quizItem.id];
      const hasWrongAttempts = (state?.attempts && state.attempts > 0) || (state?.wrongOptions && state.wrongOptions.length > 0);
      if (state?.isCorrect && !hasWrongAttempts) {
        correctCount++;
      }
    });

    return Math.round((correctCount / quizzes.length) * 100);
  };

  const calcPointsEarned = (endIdx: number, fromStart = false, states = interactionStatesRef.current) => {
    let startIdx = 0;
    if (!fromStart) {
      for (let i = endIdx - 1; i >= 0; i--) {
        if (interactions[i].isPhaseStop) { startIdx = i + 1; break; }
      }
    }
    let total = 0;
    for (let i = startIdx; i <= endIdx; i++) {
      const item = interactions[i];
      if (item) {
        total += states[item.id]?.pointsEarned || 0;
      }
    }
    return total;
  };

  const getResumeIndex = useCallback(() => {
    if (!currentProgress || !currentProgress.lastCompletedInteractionId) return -1;
    const idx = interactions.findIndex((i: any) => i.id === currentProgress.lastCompletedInteractionId);
    if (idx === -1) return -1;
    return Math.min(idx + 1, interactions.length - 1);
  }, [currentProgress, interactions]);

  const handleGreetingComplete = () => {
    setIsGreetingDone(true);
    setTimeout(() => {
      setShowInteractions(true);
      if (currentInteractionIndex === -1) {
        const resumeIdx = getResumeIndex();
        if (resumeIdx !== -1) {
          setCurrentInteractionIndex(resumeIdx);
        } else {
          setCurrentInteractionIndex(0);
        }
      }
      setIsResuming(false);
    }, 1000);
  };

  const handleNextInteraction = async (isEligibleForReward?: any, completedItem?: any) => {
    const currentItem = completedItem || interactions[currentInteractionIndex];
    if (!currentItem) return;

    const nextIdx = currentInteractionIndex + 1;
    const isPreviousInteraction = currentItem.id !== interactions[currentInteractionIndex].id;
    const state = interactionStates[currentItem.id] || {};

    const proceed = async (manualStateUpdates: any = {}) => {
      const now = Date.now();
      const currentId = currentItem.id;

      // Update state with completion timestamp
      setInteractionStates((prev: any) => ({
        ...prev,
        [currentId]: { ...prev[currentId], ...manualStateUpdates, completedAt: now }
      }));

      const nextPercentage = Math.round(((currentInteractionIndex + 1) / totalItems) * 100);

      // Build a local version of interactionStates that includes current updates for immediate use
      const statesForCalc = {
        ...interactionStatesRef.current,
        [currentId]: { ...(interactionStatesRef.current[currentId] || {}), ...manualStateUpdates, completedAt: now }
      };

      // Get filtered states using the augmented state
      const filteredStates = { ...getFilteredStates() };
      filteredStates[currentId] = { ...(filteredStates[currentId] || {}), ...manualStateUpdates, completedAt: now };

      updateProgress(currentId, nextPercentage, filteredStates);
      debouncedSync();

      try {
        if (window.history.state && lessonData?.id) {
          const currentUsr = window.history.state.usr || {};
          if (!currentUsr.resume) {
            window.history.replaceState(
              {
                ...window.history.state,
                usr: { ...currentUsr, resume: true, lessonId: lessonData.id }
              },
              ''
            );
          }
        }
      } catch (e) {
        // Ignore history state replacement errors
      }

      if (isPreviousInteraction) {
        return;
      }

      const isLastInteraction = nextIdx >= interactions.length;
      const currentPhase = lessonData.phases?.find((p: any) => p.step_num === currentItem.stepNumber) ||
        (currentItem.phaseIndex ? lessonData.phases?.[currentItem.phaseIndex - 1] : null);
      const phaseExerciseList = currentPhase?.exercise_list || [];

      if (isLastInteraction) {
        const lessonId = lessonData?.id;
        if (lessonId) {
          syncToDB(lessonId, totalItems, filteredStates);
        }
        const optimisticPoints = gamificationStore.getState().pointsConfig['lesson_complete'] || 120;
        navigate(ROUTERS_PATH.LESSON_SUMMARY, {
          state: {
            fullTopic,
            levelName,
            lessonId,
            isFinalSummary: true,
            exerciseList: phaseExerciseList,
            lessonData,
            learningTimeMs: calcLearningTime(currentInteractionIndex, true, statesForCalc),
            interactionStates: filteredStates,
            optimisticStats: {
              pointsEarned: optimisticPoints + calcPointsEarned(currentInteractionIndex, true, statesForCalc),
              accuracy: calcAccuracy(currentInteractionIndex, true, statesForCalc),
              completedCount: currentInteractionIndex + 1,
              totalCount: totalItems
            }
          }
        });
        return;
      }

      if (currentItem.isPhaseStop) {
        const lessonId = lessonData?.id;
        if (lessonId) {
          syncToDB(lessonId, totalItems, filteredStates);
        }
        const optimisticPoints = gamificationStore.getState().pointsConfig['lesson_phase_complete'] || 50;
        navigate(ROUTERS_PATH.LESSON_SUMMARY, {
          state: {
            fullTopic,
            levelName,
            lessonId,
            isPhaseSummary: true,
            exerciseList: phaseExerciseList,
            phaseIndex: currentItem.phaseIndex,
            currentStepIndex: currentInteractionIndex,
            lessonData,
            learningTimeMs: calcLearningTime(currentInteractionIndex, false, statesForCalc),
            interactionStates: filteredStates,
            optimisticStats: {
              pointsEarned: optimisticPoints + calcPointsEarned(currentInteractionIndex, false, statesForCalc),
              accuracy: calcAccuracy(currentInteractionIndex, false, statesForCalc),
              completedCount: currentInteractionIndex + 1,
              totalCount: totalItems
            }
          }
        });
        return;
      }

      setCurrentInteractionIndex(nextIdx);
      setIsResuming(false);
    };

    if (isEligibleForReward === true && !state.isRewarded && currentItem.type === 'video') {
      const optimisticPoints = lessonGamificationService.awardVideoWatched(
        lessonData?.id,
        1,
        undefined,
        (correction) => {
          const currentPts = interactionStatesRef.current[currentItem.id]?.pointsEarned ?? optimisticPoints;
          const updatedVideoState = {
            ...(interactionStatesRef.current[currentItem.id] || {}),
            pointsEarned: Math.max(0, currentPts + correction)
          };
          interactionStatesRef.current[currentItem.id] = updatedVideoState;
          setInteractionStates((prev: any) => ({
            ...prev,
            [currentItem.id]: {
              ...prev[currentItem.id],
              pointsEarned: Math.max(0, (prev[currentItem.id]?.pointsEarned ?? optimisticPoints) + correction)
            }
          }));
          debouncedSync();
        }
      );

      // Update state IMMEDIATELY to show banner with optimistic points
      const rewardVideoState = {
        ...(interactionStatesRef.current[currentItem.id] || {}),
        isRewarded: true,
        pointsEarned: optimisticPoints
      };
      interactionStatesRef.current[currentItem.id] = rewardVideoState;
      setInteractionStates((prev: any) => ({
        ...prev,
        [currentItem.id]: { ...prev[currentItem.id], isRewarded: true, pointsEarned: optimisticPoints }
      }));

      playWinSound();

      // Delay ONLY the navigation/next step
      setTimeout(() => {
        proceed();
      }, 2000);
    } else {
      let manualUpdates: any = {};
      const isTeacherContent = currentItem.type === 'content';

      if (isTeacherContent && !state.isStepCompleteAwarded) {
        const optimisticPoints = lessonGamificationService.awardStepComplete(
          lessonData?.id,
          currentItem.stepNumber,
          currentItem.phaseIndex,
          (correction) => {
            const currentPts = interactionStatesRef.current[currentItem.id]?.pointsEarned ?? optimisticPoints;
            const updatedStepState = {
              ...(interactionStatesRef.current[currentItem.id] || {}),
              pointsEarned: Math.max(0, currentPts + correction)
            };
            interactionStatesRef.current[currentItem.id] = updatedStepState;
            setInteractionStates((prev: any) => ({
              ...prev,
              [currentItem.id]: {
                ...prev[currentItem.id],
                pointsEarned: Math.max(0, (prev[currentItem.id]?.pointsEarned ?? optimisticPoints) + correction)
              }
            }));
            debouncedSync();
          }
        );

        manualUpdates = {
          isStepCompleteAwarded: true,
          pointsEarned: (state.pointsEarned || 0) + optimisticPoints
        };
      }
      proceed(manualUpdates);
    }
  };

  const handleQuizAnswer = async (isCorrect: boolean, selectedOption?: string, quizItem?: any) => {
    const currentItem = quizItem || interactions[currentInteractionIndex];
    if (!currentItem) return;

    const currentState = interactionStatesRef.current[currentItem.id] || {};
    
    // If they already answered correctly, block further actions
    if (currentState.isAnswered && currentState.isCorrect) return;

    if (!isCorrect) {
      // Wrong attempt
      const wrongOpts = currentState.wrongOptions || [];
      const newWrongOpts = selectedOption && !wrongOpts.includes(selectedOption) 
        ? [...wrongOpts, selectedOption] 
        : wrongOpts;
      const newAttempts = (currentState.attempts || 0) + 1;

      const updatedState = {
        ...currentState,
        wrongOptions: newWrongOpts,
        attempts: newAttempts,
        isAnswered: false,
        isCorrect: false,
        isClueAsked: false
      };

      // Sync ref immediately so debouncedSync and getFilteredStates have it synchronously
      interactionStatesRef.current[currentItem.id] = updatedState;

      setInteractionStates((prev: any) => ({
        ...prev,
        [currentItem.id]: updatedState
      }));

      debouncedSync();
      return;
    }

    // Correct attempt
    const prevAttempts = currentState.attempts || 0;

    const updatedState = {
      ...currentState,
      isAnswered: true,
      isCorrect: true,
      selectedOption
    };

    interactionStatesRef.current[currentItem.id] = updatedState;

    setInteractionStates((prev: any) => ({
      ...prev,
      [currentItem.id]: updatedState
    }));

    if (prevAttempts === 0) {
      // First try correct - award points normally!
      if (!interactionStates[currentItem.id]?.isRewarded) {
        const state = interactionStates[currentItem.id] || {};
        const isHintUsed = state.isClueAsked || state.isExplanationAsked;
        const actionKey = isHintUsed ? 'quiz_correct_with_hint' : 'quiz_correct';

        const quizItemIndex = interactions.findIndex((i: any) => i.id === currentItem.id);
        let streak = 0;
        for (let i = 0; i < quizItemIndex; i++) {
          const item = interactions[i];
          if (item.type === 'quiz') {
            const prevState = interactionStates[item.id];
            if (prevState?.isAnswered) {
              const wasFirstTimeCorrect = prevState.isCorrect && 
                (!prevState.attempts || prevState.attempts === 0) && 
                (!prevState.wrongOptions || prevState.wrongOptions.length === 0);
              if (wasFirstTimeCorrect) streak++;
              else streak = 0;
            }
          }
        }
        const currentStreakCount = streak + 1;

        const { optimisticPoints: totalOptimistic, achievedStreak } = lessonGamificationService.awardQuizCorrect(
          lessonData?.id,
          currentItem.stepNumber,
          currentItem.phaseIndex,
          isHintUsed,
          currentStreakCount,
          undefined,
          (correction) => {
            const currentPts = interactionStatesRef.current[currentItem.id]?.pointsEarned ?? totalOptimistic;
            const updatedInteractionState = { 
              ...(interactionStatesRef.current[currentItem.id] || {}), 
              pointsEarned: Math.max(0, currentPts + correction)
            };
            interactionStatesRef.current[currentItem.id] = updatedInteractionState;
            setInteractionStates((prev: any) => ({
              ...prev,
              [currentItem.id]: updatedInteractionState
            }));
            debouncedSync();
          }
        );

        const rewardState = {
          ...updatedState,
          isRewarded: true,
          successMessageCount: (chatThreads[currentItem.id] || []).length,
          pointsEarned: (updatedState.pointsEarned || 0) + totalOptimistic,
          streakAchieved: achievedStreak
        };
        interactionStatesRef.current[currentItem.id] = rewardState;
        setInteractionStates((prev: any) => ({
          ...prev,
          [currentItem.id]: rewardState
        }));
        setIsResuming(false);
      }
    } else {
      // Subsequent try correct - no points!
      const finalState = {
        ...updatedState,
        isRewarded: true,
        pointsEarned: 0,
        successMessageCount: (chatThreads[currentItem.id] || []).length
      };
      interactionStatesRef.current[currentItem.id] = finalState;
      setInteractionStates((prev: any) => ({
        ...prev,
        [currentItem.id]: finalState
      }));
      setIsResuming(false);
    }

    // Always sync database immediately / debounced on answer select
    debouncedSync();
  };

  return {
    handleNextInteraction,
    handleGreetingComplete,
    handleQuizAnswer
  };
};
// Agent synced
